'use client';
import React, { useRef, useEffect, useState } from 'react';
import { Input, InputRef } from 'antd';
import { cn } from '@/lib/utils';

interface CoDigitProps {
    disabled?: boolean;
    value: string[];
    onChange: (value: string[]) => void;
    onComplete?: (value: string) => void;
    error?: boolean;
    onErrorChange?: (error: boolean) => void;
    errorMsg?: string;
    onErrorMsgChange?: (errorMsg: string) => void;
    idPrefix?: string;
    className?: string;
    inputClassName?: string;
    type?: 'number' | 'alphanumeric';
}

export default function CoDigitInput({
    disabled = false,
    value,
    onChange,
    onComplete,
    error = false,
    onErrorChange,
    errorMsg = '',
    onErrorMsgChange,
    idPrefix = 'digit',
    className = '',
    inputClassName = '',
    type = 'number',
}: CoDigitProps) {
    const firstDigitInputRef = useRef<HTMLInputElement>(null);
    const [shouldShake, setShouldShake] = useState(false);
    const handleDigitChange = (index: number, val: string) => {
        if (error || !!errorMsg) {
            onErrorChange && onErrorChange(false);
            onErrorMsgChange && onErrorMsgChange('');
        };
        if (val.length > 1) val = val.slice(-1);
        // 根据 type 类型验证输入
        if (val) {
            if (type === 'number') {
                // 只允许数字
                if (!/^\d$/.test(val)) return;
            } else {
                // 允许字母和数字
                if (!/^[a-zA-Z0-9]$/.test(val)) return;
            }
        }
        const next = [...value];
        next[index] = val;
        onChange(next);
        if (val && index < 5) {
            const el = document.getElementById(`${idPrefix}-${index + 1}`);
            (el as HTMLInputElement)?.focus();
        }
        if (val && next.join('').length === 6 && onComplete) {
            onComplete(next.join(''));
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Backspace') return;
        if (value[index]) return;
        if (index > 0) {
            e.preventDefault();
            const next = [...value];
            next[index - 1] = '';
            onChange(next);
            const el = document.getElementById(`${idPrefix}-${index - 1}`);
            (el as HTMLInputElement)?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        // 根据 type 类型过滤粘贴内容
        let filteredText: string;
        if (type === 'number') {
            // 只保留数字
            filteredText = pastedText.replace(/\D/g, '').slice(0, 6);
        } else {
            // 保留字母和数字
            filteredText = pastedText.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6);
        }
        if (filteredText.length > 0) {
            const next = [...value];
            for (let i = 0; i < filteredText.length; i++) {
                next[i] = filteredText[i];
            }
            onChange(next);

            if (error || !!errorMsg) {
                onErrorChange && onErrorChange(false);
                onErrorMsgChange && onErrorMsgChange('');
            }

            if (filteredText.length === 6 && onComplete) {
                setTimeout(() => onComplete(next.join('')), 0);
            } else {
                const focusIndex = Math.min(filteredText.length, 5);
                const el = document.getElementById(`${idPrefix}-${focusIndex}`);
                (el as HTMLInputElement)?.focus();
            }
        }
    };

    const clearInput = () => {
        onChange(['', '', '', '', '', '']);
        setTimeout(() => (firstDigitInputRef.current as HTMLInputElement)?.focus(), 0);
    }
    // 报错的话，清空输入框，同时聚焦在第一个输入框，并触发抖动动画
    useEffect(() => {
        if (error || !!errorMsg) {
            clearInput();
            // 触发抖动动画
            setShouldShake(true);
            const timer = setTimeout(() => {
                setShouldShake(false);
            }, 500); // 动画持续时间
            return () => clearTimeout(timer);
        }
    }, [error, errorMsg]);
    // 聚焦第一个输入框
    useEffect(() => {
        clearInput();
    }, []);

    return (
        <div className={cn('relative pb-5', shouldShake && 'animate-shake')}>
            <div className={cn('flex w-full justify-between', className)}>
                {[0, 1, 2, 3, 4, 5].map(i => (
                    <Input
                        key={i}
                        ref={i === 0 ? (firstDigitInputRef as React.Ref<InputRef>) : undefined}
                        id={i === 0 ? `${idPrefix}-0` : `${idPrefix}-${i}`}
                        maxLength={1}
                        value={value[i]}
                        disabled={disabled}
                        onChange={e => handleDigitChange(i, e.target.value)}
                        onKeyDown={e => handleKeyDown(i, e)}
                        onPaste={handlePaste}
                        autoComplete="off"
                        status={(error || !!errorMsg) ? 'error' : undefined}
                        className={cn(
                            'w-[60px] h-[60px] text-center text-lg border-button-border text-font-primary focus:border-green-500 [&_.ant-input]:!text-center [&_.ant-input]:!h-full',
                            inputClassName
                        )}
                    />
                ))}
            </div>
            {errorMsg && <div className='absolute bottom-0 left-0 text-xs text-red-500'>{errorMsg}</div>}
        </div>
    );
}
